import { err, ok, type Result } from '../result.ts';
import { addDays, compareDays, type CalendarDay } from '../time.ts';

// Data-subject requests under the Data Protection Act 2019
// (docs/plans/phase2-records-data-rights.md §4): due dates, the words the queue shows, and
// which outcomes a request may be closed with.

export const RECORDS_REQUEST_KINDS = [
  'access',
  'correction',
  'objection',
  'restriction',
  'deletion',
] as const;
export type RecordsRequestKind = (typeof RECORDS_REQUEST_KINDS)[number];

export const RECORDS_REQUEST_STATUSES = ['received', 'verified', 'closed'] as const;
export type RecordsRequestStatus = (typeof RECORDS_REQUEST_STATUSES)[number];

export const RECORDS_REQUEST_OUTCOMES = [
  'fulfilled',
  'partly_fulfilled',
  'declined',
  'withdrawn',
] as const;
export type RecordsRequestOutcome = (typeof RECORDS_REQUEST_OUTCOMES)[number];

/** TODO(legal-verify) R3: who may ask on a patient's behalf. */
export const RECORDS_REQUESTER_RELATIONSHIPS = [
  'self',
  'parent_or_guardian',
  'authorised_representative',
] as const;
export type RecordsRequesterRelationship = (typeof RECORDS_REQUESTER_RELATIONSHIPS)[number];

export const RECORDS_REQUEST_CHANNELS = ['in_person', 'written', 'email'] as const;
export type RecordsRequestChannel = (typeof RECORDS_REQUEST_CHANNELS)[number];

export const RECORDS_CORRECTION_AREAS = ['demographics', 'clinical'] as const;
export type RecordsCorrectionArea = (typeof RECORDS_CORRECTION_AREAS)[number];

/** TODO(legal-verify) R4: what identity evidence is enough. */
export const IDENTITY_EVIDENCE = [
  'national_id',
  'passport',
  'birth_certificate',
  'alien_id',
  'known_to_staff',
  'other',
] as const;
export type IdentityEvidence = (typeof IDENTITY_EVIDENCE)[number];

export const RECORD_RELEASE_FORMATS = ['printed_summary', 'machine_readable'] as const;
export type RecordReleaseFormat = (typeof RECORD_RELEASE_FORMATS)[number];

/**
 * Calendar days from the day a request is received to the day it must be answered.
 * TODO(legal-verify) R1: provisional reading of the Data Protection (General) Regulations
 * 2021; whether the days are calendar or working days, and whether they can be extended.
 */
export const STATUTORY_DAYS: Readonly<Record<RecordsRequestKind, number>> = {
  access: 7,
  correction: 14,
  objection: 14,
  restriction: 14,
  deletion: 14,
};

/** The East Africa day a request received on `receivedDay` must be answered by. */
export function requestDueOn(kind: RecordsRequestKind, receivedDay: CalendarDay): CalendarDay {
  return addDays(receivedDay, STATUTORY_DAYS[kind]);
}

export type RecordsRequestState = 'open' | 'due_today' | 'overdue' | 'closed';

/** How the queue describes a request on `today`. */
export function requestState(
  dueOn: CalendarDay,
  today: CalendarDay,
  status: RecordsRequestStatus,
): RecordsRequestState {
  if (status === 'closed') return 'closed';
  const c = compareDays(today, dueOn);
  if (c < 0) return 'open';
  return c === 0 ? 'due_today' : 'overdue';
}

/**
 * The outcomes a request may be closed with. Before identity is verified nothing can be
 * released or changed, so only "declined" (for example, identity couldn't be verified) and
 * "withdrawn". A deletion request is never fulfilled: clinical data is never hard-deleted
 * (domain rule 6); it is answered with the retention explanation (R5).
 */
export function allowedOutcomes(
  kind: RecordsRequestKind,
  status: RecordsRequestStatus,
): readonly RecordsRequestOutcome[] {
  if (status === 'closed') return [];
  if (status === 'received' || kind === 'deletion') return ['declined', 'withdrawn'];
  return RECORDS_REQUEST_OUTCOMES;
}

export type CloseCheckErrorCode =
  | 'records.request_closed'
  | 'records.outcome_not_allowed'
  | 'records.release_required'
  | 'records.change_required';

export interface CloseFacts {
  /** Releases of the record made for this request. */
  readonly releases: number;
  /** Changes to the record linked to this request. */
  readonly linkedChanges: number;
}

/**
 * Whether a request may be closed with `outcome`: an access request is fulfilled only once
 * something was released, a correction only once a change is linked to it.
 */
export function closeCheck(
  kind: RecordsRequestKind,
  status: RecordsRequestStatus,
  outcome: RecordsRequestOutcome,
  facts: CloseFacts,
): Result<RecordsRequestOutcome, CloseCheckErrorCode> {
  if (status === 'closed') return err('records.request_closed');
  if (!allowedOutcomes(kind, status).includes(outcome)) return err('records.outcome_not_allowed');
  const done = outcome === 'fulfilled' || outcome === 'partly_fulfilled';
  if (done && kind === 'access' && facts.releases === 0) return err('records.release_required');
  if (done && kind === 'correction' && facts.linkedChanges === 0)
    return err('records.change_required');
  return ok(outcome);
}

/**
 * The default answer to a deletion request. TODO(legal-verify) R5: the wording, and the
 * retention period the facility should quote.
 */
export const RETENTION_ANSWER =
  'Health records must be kept for the period the law and the facility’s retention policy ' +
  'require. Your request is recorded, and your record is not deleted. You may ask for a ' +
  'correction if something in it is wrong.';
